import React, { useEffect, useState } from 'react';
import {
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  TextField,
} from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { buttonPrimaryColor } from 'theme/theme';
import clsx from 'clsx';

import SearchIcon from '@material-ui/icons/Search';

const useStyles = makeStyles(() => ({
  paperRoot: {
    position: 'absolute',
    zIndex: 9999,
    padding: '0',
    top: '50px',
    width: '100%',
    borderRadius: '0 0 4px 4px',
    boxShadow: 'none',
    borderTop: 'none',
    borderRight: `2px solid ${buttonPrimaryColor()}`,
    borderBottom: `2px solid ${buttonPrimaryColor()}`,
    borderLeft: `2px solid ${buttonPrimaryColor()}`,
  },
  container: {
    position: 'relative',
  },
  fieldWithDD: {
    borderColor: buttonPrimaryColor(),
    '& .MuiOutlinedInput-root': {
      fieldset: {
        borderColor: buttonPrimaryColor(),
      },
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      paddingRight: 0,
    },
  },
  dropdown: {
    paddingRight: '0',
    paddingLeft: '0',
    margin: '0',
  },
  dropdownOption: {
    display: 'block',
    listStyle: 'none',
    padding: '0.5rem 0.8rem',
    cursor: 'pointer',
    color: '#000',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

type AutocompleteProps<OptionType> = {
  onSelect?: (option?: OptionType) => void;
  getOptionLabel: (option: OptionType) => string;
  options: OptionType[];
  onChange?: (name: string) => void;
  value?: OptionType;
  label?: string;
};

function Autocomplete<OptionType>(
  props: AutocompleteProps<OptionType>,
): React.ReactElement {
  const { onSelect, onChange, value, label, options, getOptionLabel } = props;
  const classes = useStyles();
  const [filteredOptions, setFilteredOptions] = useState<OptionType[]>([]);

  const [storedValue, setValue] = useState('');
  const [sortedOptions, setOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    setValue(value ? getOptionLabel(value) : '');
  }, [value]);

  useEffect(() => {
    // sort options by lowercase label
    setOptions(
      options.sort((a, b) =>
        getOptionLabel(a)
          .toLowerCase()
          .localeCompare(getOptionLabel(b).toLowerCase()),
      ),
    );
  }, [options]);

  const filterOptions = (value: string) => {
    const lowerCaseValue = value.toLowerCase();
    return sortedOptions.filter(
      (option) =>
        getOptionLabel(option).toLowerCase().indexOf(lowerCaseValue) > -1,
    );
  };

  const handleFocus = () => {
    if (storedValue.length > 0) {
      setFilteredOptions(filterOptions(storedValue));
    }
  };

  const handleClickAway = () => {
    const lowerCaseValue = storedValue.toLowerCase();
    const option = filteredOptions.find(
      (option) => getOptionLabel(option).toLowerCase() === lowerCaseValue,
    );
    if (option) {
      handleSelect(option);
    } else {
      setValue(value ? getOptionLabel(value) : '');
      setFilteredOptions([]);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement | undefined>,
  ) => {
    if (e.key === 'Tab') {
      // e.preventDefault();
      handleClickAway();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // select closest match on submit
    if (storedValue.length === 0) {
      onSelect && onSelect(); // clear selection
    } else {
      const filtered = filterOptions(storedValue);
      if (filtered.length > 0) {
        handleSelect(filtered[0]);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = e.target;

    setValue(value);
    onChange && onChange(value);

    if (value.length === 0) {
      setFilteredOptions([]);
    } else {
      setFilteredOptions(filterOptions(value));
    }
  };

  const handleSelect = (option: OptionType) => {
    setValue(getOptionLabel(option));
    if (value !== option) {
      onSelect && onSelect(option);
    }
    setFilteredOptions([]);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={classes.container}>
        <form noValidate onSubmit={handleSubmit}>
          <TextField
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            type="text"
            variant="outlined"
            margin="none"
            fullWidth
            label={label}
            value={storedValue}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    tabIndex={-1}
                    type="submit"
                    disabled={options.length === 0}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className={clsx(
              filteredOptions.length > 0 ? classes.fieldWithDD : '',
            )}
            classes={{ root: classes.textField }}
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            disabled={options.length === 0}
          />
        </form>

        {filteredOptions.length > 0 && (
          <Paper className={classes.paperRoot}>
            <ul className={classes.dropdown}>
              {filteredOptions?.map((option, index) => {
                return (
                  <li
                    className={classes.dropdownOption}
                    key={index}
                    onClick={() => handleSelect(option)}
                  >
                    {getOptionLabel(option)}
                  </li>
                );
              })}
            </ul>
          </Paper>
        )}
      </div>
    </ClickAwayListener>
  );
}

export { Autocomplete };
